import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';

/* Helpers */
import get from 'lodash.get';
import {
    NUMBER_OF_PRODUCT_IMPRESSIONS,
    trackEcommerceProductImpressions,
    trackEcommerceProductClick,
} from '../../utils/tracking/searchBrowse/ecommerceTracking';

class SbSharedItemTrackingProviderComponent extends React.Component {
    constructor() {
        super();

        this.state = {
            shouldFireImpression: false,
        };

        this.fireItemImpressionTracking = this.fireItemImpressionTracking.bind(this);
        this.fireItemClickTracking = this.fireItemClickTracking.bind(this);
        this.trackedItemImpressionIndexes = [];
    }

    componentDidUpdate(prevProps, prevState) {
        /**
         * If shouldFireImpression is false wait for semanticSearchTerm to be checked
         * Once set, continue to fire tracking based on displayUriRef changes
         */
        if (!this.state.shouldFireImpression && this.props.hasCheckedForSemanticSearchTerm) {
            this.setState({ shouldFireImpression: true });
        } else if (
            (this.state.shouldFireImpression && !prevState.shouldFireImpression) ||
            this.props.itemSearch.displayUriRef !== prevProps.itemSearch.displayUriRef
        ) {
            /**
             * Assume the page changed and reset tracking data so that we're able to fire tracking
             * for the new set of items.
             */
            this.trackedItemImpressionIndexes = [];
            this.fireItemImpressionTracking(0);
        }
    }

    trackProductImpression(startIndex) {
        const { semanticSearchTerm } = this.props;
        const items = get(this.props, 'itemSearch.edges');
        const pageType = get(this.props, 'itemSearch.pageType');
        const searchTerm = get(this.props, 'itemSearch.searchTerm');

        trackEcommerceProductImpressions({
            items,
            pageType,
            searchTerm,
            startIndex,
            semanticSearchTerm,
        });
    }

    fireItemImpressionTracking(index) {
        /**
         * Group items from index 0 to 19 to group with the number `0`, from 20 to 39 to group
         * 1 and so on.
         */
        const impressionGroup = Math.floor(index / NUMBER_OF_PRODUCT_IMPRESSIONS);
        /**
         * Very important to track indexes that have already fired - `react-lazy-load` can fire
         * `contentVisible` handler multiple times for the same item.
         */
        if (!this.trackedItemImpressionIndexes.includes(impressionGroup)) {
            this.trackProductImpression(impressionGroup * NUMBER_OF_PRODUCT_IMPRESSIONS);
            this.trackedItemImpressionIndexes.push(impressionGroup);
        }
    }

    fireItemClickTracking(index) {
        const searchTerm = get(this.props, 'itemSearch.searchTerm');
        const pageType = get(this.props, 'itemSearch.pageType');
        const items = get(this.props, 'itemSearch.edges');
        const item = get(items, `[${index}].node.item`) || get(items, `[${index}].node`);
        const { semanticSearchTerm } = this.props;

        trackEcommerceProductClick({
            item,
            index,
            pageType,
            searchTerm,
            semanticSearchTerm,
        });
    }

    render() {
        return this.props.children({
            fireItemImpressionTracking: this.fireItemImpressionTracking,
            fireItemClickTracking: this.fireItemClickTracking,
        });
    }
}

SbSharedItemTrackingProviderComponent.propTypes = {
    children: PropTypes.func,
    hasCheckedForSemanticSearchTerm: PropTypes.bool,
    semanticSearchTerm: PropTypes.string,
    itemSearch: PropTypes.object.isRequired,
};

/*
  Fields necessary for ecommerce tracking...
  serviceId
  title
  seller { serviceId }
  categoryPath
  contemporaryTrackingString
  displayInfo(buyerId: $userId) @include(if: $isClient) {
    pricing {
        showNet
        showSale
    }
  }
  pricing {
    amount {
        convertedAmountList {
            amount
            currency
        }
    }
    salePrice {
        convertedAmountList {
            amount
            currency
        }
    }
    netPrice {
        convertedAmountList {
            amount
            currency
        }
    }
  }
*/
export const SbSharedItemTrackingProvider = createFragmentContainer(
    SbSharedItemTrackingProviderComponent,
    {
        itemSearch: graphql`
            fragment SbSharedItemTrackingProvider_itemSearch on ItemSearchQueryConnection {
                pageType
                searchTerm
                displayUriRef
                edges {
                    node {
                        item {
                            serviceId
                            title
                            categoryPath
                            browseUrl
                            categoryCode
                            contemporaryTrackingString
                            seller {
                                serviceId
                            }
                            displayPriceTracking: displayPrice(
                                page: searchAndBrowse
                                isTrade: $isTrade
                            ) {
                                convertedAmountList {
                                    amount
                                    currency
                                }
                                type
                            }
                        }
                    }
                }
            }
        `,
    }
);
